import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight } from 'react-icons/fa';
import pslv from '../../assets/pslv.png';
import './HeroSection.css';

const contentVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.2 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  }
};

const panelVariants = {
  hidden: { opacity: 0, x: 40, scale: 0.96 },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { duration: 0.95, ease: [0.22, 1, 0.36, 1], delay: 0.28 }
  }
};

const HeroSection = () => {
  const scrollToNext = () => {
    const nextSection = document.querySelector('#about');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="hero" aria-label="Antaral Aerospace">
      <div className="hero__atmosphere" aria-hidden="true">
        <div className="hero__stars" />
        <div className="hero__glow hero__glow--cyan" />
        <div className="hero__glow hero__glow--amber" />
        <div className="hero__horizon" />
        <div className="hero__arc" />
      </div>

      <div className="hero__container">
        <motion.div
          className="hero__content"
          variants={contentVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.p className="hero__eyebrow" variants={itemVariants}>
            Aerospace Systems · Design phase 2025
          </motion.p>

          <motion.h1 className="hero__brand" variants={itemVariants}>
            Antaral
          </motion.h1>

          <motion.p className="hero__headline" variants={itemVariants}>
            Built for orbit.
          </motion.p>

          <motion.p className="hero__copy" variants={itemVariants}>
            Launch vehicles and CubeSat programs designed from the ground up —
            honest engineering on the long road to first flight.
          </motion.p>

          <motion.div className="hero__actions" variants={itemVariants}>
            <button type="button" className="btn btn-primary" onClick={scrollToNext}>
              Explore the work
              <FaArrowRight />
            </button>
            <Link to="/contact" className="btn btn-secondary">
              Talk to our team
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero__visual"
          variants={panelVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="hero__panel">
            <div className="hero__panel-top">
              <span className="hero__panel-label">RPSLV — Concept design</span>
              <span className="hero__panel-status">Concept phase</span>
            </div>

            <div className="hero__vehicle-visual">
              <motion.img
                src={pslv}
                alt="RPSLV launch vehicle concept"
                className="hero__vehicle-img"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 7, ease: 'easeInOut', repeat: Infinity }}
              />
              <div className="hero__vehicle-orbit hero__vehicle-orbit--1" aria-hidden="true" />
              <div className="hero__vehicle-orbit hero__vehicle-orbit--2" aria-hidden="true" />
              <div className="hero__vehicle-crosshair" aria-hidden="true" />
              <div className="hero__vehicle-plume" aria-hidden="true" />
            </div>

            <div className="hero__vehicle-specs">
              <div className="hero__spec-row">
                <span className="hero__spec-key">Payload class</span>
                <span className="hero__spec-val">450 kg</span>
              </div>
              <div className="hero__spec-row">
                <span className="hero__spec-key">Target orbits</span>
                <span className="hero__spec-val">LEO · SSO</span>
              </div>
              <div className="hero__spec-row">
                <span className="hero__spec-key">Program phase</span>
                <span className="hero__spec-val">Architecture &amp; Design</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.button
        type="button"
        className="hero__scroll"
        onClick={scrollToNext}
        aria-label="Scroll to about section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
      >
        <span className="hero__scroll-line" aria-hidden="true" />
        <span>Scroll</span>
      </motion.button>
    </section>
  );
};

export default HeroSection;
